import * as React from 'react'
import { useSwipeable } from 'react-swipeable'
import { useLocation } from 'react-router-dom'
import { useSnapshot } from 'valtio'
import { controlState } from '../../store/controlState.ts'
import { drawerState } from '../../store/drawerState.ts'
import { useViewTransitionNavigate } from '../../hooks/useViewTransitionNavigate.ts'

// Width of the left-edge swipe zone in pixels
const EDGE_THRESHOLD = 30

// Routes where back-swipe is disabled (root-level, nothing to go back to)
const rootRoutes = ['/map', '/history']

/**
 * SwipeBack — left-edge swipe-to-go-back gesture for PWA on mobile.
 * Renders an invisible overlay that captures swipes starting from the left edge.
 * Only active on mobile devices.
 */
export const SwipeBack: React.FC = () => {
    const isMobile = useSnapshot(controlState).isOnMobileDevice
    const menuOpen = useSnapshot(controlState).menuOpen
    const drawerOpen = useSnapshot(drawerState).drawerOpen
    const location = useLocation()
    const { goBack } = useViewTransitionNavigate()

    const isRootRoute = rootRoutes.includes(location.pathname)

    const handlers = useSwipeable({
        onSwipedRight: (eventData) => {
            // Only trigger if swipe started from left edge
            const startX = eventData.initial[0]
            if (startX > EDGE_THRESHOLD) return

            // Don't trigger if drawers/menus are open or on root routes
            if (menuOpen || drawerOpen || isRootRoute) return

            goBack()
        },
        trackMouse: false,
        trackTouch: true,
        delta: 50, // Minimum swipe distance to trigger
        preventScrollOnSwipe: false,
    })

    // Don't render on desktop or root routes
    if (!isMobile) return null

    return (
        <div
            {...handlers}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: `${EDGE_THRESHOLD}px`,
                height: '90vh', // Don't cover the TapBar
                zIndex: 99999,
                touchAction: 'pan-y', // Allow vertical scroll, capture horizontal
            }}
            aria-hidden="true"
        />
    )
}
